import { Action, createReducer, on } from "@ngrx/store";

import {} from "@hermes/api-model-virtual-try-on";

import * as vtoActions from "../actions/vto.actions";
import { VTO_CONFIGURATION_UNDEFINED } from "../constants/vto.constant";
import {
    getLearnMoreDisplay,
    getUpdatedSkuApplied,
    getVtoSubcategoryByType,
    isSku,
    prepareCategories,
    updateCategories,
    updatedSubcategoryOnActiveCategory,
} from "../helpers/vto.helper";
import {
    LocalizedVtoCategory,
    LocalizedVtoSubcategory,
    NO_CATEGORY,
    NO_SELECT,
    NO_SHADE,
    NO_SUBCATEGORY,
} from "../model/localized-vto-category.model";
import { VtoModalStatus, VtoSdkError } from "../types/vto.types";

import { initialState, VtoState } from "./vto-state";

export const vtoFeatureKey = "vto";

const fetchVtoConfigurationSuccessReducer = (
    state: VtoState,
    action: ReturnType<typeof vtoActions.fetchVtoConfigurationSuccess>,
): VtoState => ({
    ...state,
    vtoConfig: action.vtoConfig ?? VTO_CONFIGURATION_UNDEFINED,
});

// relaunch camera if camera is loaded or on error
const changeScreenOrientationReducer = (state: VtoState): VtoState =>
    state.modalStatus === VtoModalStatus.CAMERA_LOADED ||
    state.modalStatus === VtoModalStatus.ON_ERROR
        ? {
              ...state,
              modalStatus: VtoModalStatus.CAMERA_LOADING,
              sdkError: VtoSdkError.NO_ERROR,
          }
        : state;

const updateSelectedSkuVariantOnDisplayReducer = (
    state: VtoState,
    {
        currentVtoSku,
    }: ReturnType<typeof vtoActions.updateSelectedSkuVariantOnDisplay>,
): VtoState => {
    const activeCategory: LocalizedVtoCategory = { ...state.activeCategory };
    const activeSubCategory: LocalizedVtoSubcategory = {
        ...state.activeSubcategory,
    };
    let skuApplied = isSku(state.currentSkuType)
        ? {
              ...state.skuApplied,
              [state.currentSkuType]: "",
          }
        : state.skuApplied;
    if (currentVtoSku === NO_SHADE) {
        activeSubCategory.selectedSkuVariant = currentVtoSku;
        activeCategory.selectedSubcategoryType =
            activeSubCategory.localizedVtoSubcategoryType;
        const updatedCategories = updateCategories(
            [...state.localizedVtoCategories],
            activeCategory,
            activeSubCategory,
        );
        const updatedActiveCategory = updatedSubcategoryOnActiveCategory(
            activeCategory,
            activeSubCategory,
        );

        return {
            ...state,
            skuApplied,
            currentVtoSku,
            localizedVtoCategories: updatedCategories,
            activeCategory: updatedActiveCategory,
            displayLearnMoreLink: false,
            activeSubcategory: activeSubCategory,
        };
    }
    const preparedCategories = prepareCategories(
        [...state.localizedVtoCategories],
        currentVtoSku,
    );
    const currentSkuType =
        preparedCategories.activeSubcategory.vtoProducts.length > 0
            ? preparedCategories.activeSubcategory.vtoProducts[0].skuType
            : state.currentSkuType;
    skuApplied = getUpdatedSkuApplied(
        state.skuApplied,
        currentSkuType,
        currentVtoSku,
    );
    return {
        ...state,
        currentVtoSku,
        currentSkuType,
        displayLearnMoreLink: getLearnMoreDisplay(
            preparedCategories.activeSubcategory,
            currentVtoSku,
        ),
        skuApplied,
        ...preparedCategories,
    };
};

const updateActiveCategoryReducer = (
    state: VtoState,
    { vtoCategory }: ReturnType<typeof vtoActions.updateActiveCategory>,
): VtoState => {
    const activeCategory = vtoCategory;

    const activeSubcategory = getVtoSubcategoryByType(activeCategory);

    activeCategory.selectedSubcategoryType =
        activeSubcategory.localizedVtoSubcategoryType;

    const currentVtoSku = activeSubcategory.selectedSkuVariant;
    const currentSkuType = activeSubcategory.vtoProducts[0].skuType;
    const skuApplied = {
        ...state.skuApplied,
        [currentSkuType]: currentVtoSku,
    };
    return {
        ...state,
        skuApplied,
        activeCategory,
        activeSubcategory,
        currentSkuType,
        displayLearnMoreLink: getLearnMoreDisplay(
            activeSubcategory,
            currentVtoSku,
        ),
        currentVtoSku,
    };
};

const updateActiveSubcategoryReducer = (
    state: VtoState,
    { vtoSubcategory }: ReturnType<typeof vtoActions.updateActiveSubcategory>,
): VtoState => {
    const activeSubCategory = vtoSubcategory;
    if (activeSubCategory.selectedSkuVariant === NO_SELECT) {
        activeSubCategory.selectedSkuVariant =
            activeSubCategory.vtoProducts[0].sku;
    }

    const activeCategory = state.activeCategory;
    activeCategory.selectedSubcategoryType =
        activeSubCategory.localizedVtoSubcategoryType;
    const currentVtoSku = activeSubCategory.selectedSkuVariant;

    const currentSkuType = activeSubCategory.vtoProducts[0].skuType;
    const skuApplied = {
        ...state.skuApplied,
        [currentSkuType]: currentVtoSku,
    };

    return {
        ...state,
        activeSubcategory: activeSubCategory,
        activeCategory,
        skuApplied,
        currentSkuType,
        currentVtoSku,
        displayLearnMoreLink: state.activeCategory
            ? getLearnMoreDisplay(activeSubCategory, currentVtoSku)
            : state.displayLearnMoreLink,
    };
};

const fetchLocalizedVtoCategoriesReducer = (state: VtoState): VtoState => {
    if (state.localizedVtoCategories.length > 0) {
        const preparedCategories = prepareCategories(
            [...state.localizedVtoCategories],
            state.currentVtoSku,
        );
        const currentSkuType =
            preparedCategories.activeSubcategory.vtoProducts.length > 0
                ? preparedCategories.activeSubcategory.vtoProducts[0].skuType
                : state.currentSkuType;

        const skuApplied = getUpdatedSkuApplied(
            state.skuApplied,
            currentSkuType,
            state.currentVtoSku,
        );
        return {
            ...state,
            skuApplied,
            currentSkuType,
            displayLearnMoreLink: getLearnMoreDisplay(
                preparedCategories.activeSubcategory,
                state.currentVtoSku,
            ),
            ...preparedCategories,
        };
    }
    return state;
};

const fetchLocalizedVtoCategoriesSuccessReducer = (
    state: VtoState,
    {
        localizedVtoCategories,
    }: ReturnType<typeof vtoActions.fetchLocalizedVtoCategoriesSuccess>,
): VtoState => {
    const preparedCategories = prepareCategories(
        [...localizedVtoCategories],
        state.currentVtoSku,
    );

    const currentSkuType =
        preparedCategories.activeSubcategory.vtoProducts.length > 0
            ? preparedCategories.activeSubcategory.vtoProducts[0].skuType
            : state.currentSkuType;
    const skuApplied = getUpdatedSkuApplied(
        state.skuApplied,
        currentSkuType,
        state.currentVtoSku,
    );
    return {
        ...state,
        skuApplied,
        currentSkuType,
        displayLearnMoreLink: getLearnMoreDisplay(
            preparedCategories.activeSubcategory,
            state.currentVtoSku,
        ),
        ...preparedCategories,
    };
};

const vtoReducer = createReducer(
    initialState,
    on(
        vtoActions.fetchVtoConfigurationSuccess,
        fetchVtoConfigurationSuccessReducer,
    ),
    on(vtoActions.updateCameraDimension, (state, { cameraDimension }) => ({
        ...state,
        cameraDimension,
    })),
    on(vtoActions.userApprove, (state) => ({
        ...state,
        isApproval: true,
    })),
    on(vtoActions.userDisapprove, (state) => ({
        ...state,
        isApproval: false,
    })),
    on(vtoActions.closeModal, (state) => ({
        ...state,
        modalStatus: VtoModalStatus.MODAL_CLOSED,
        sdkError: initialState.sdkError,
        currentVtoSku: NO_SELECT,
    })),
    on(vtoActions.openModal, (state) => ({
        ...state,
        modalStatus: VtoModalStatus.MODAL_OPENED,
    })),
    on(vtoActions.destroyModal, (state) => ({
        ...state,
        modalStatus: initialState.modalStatus,
        sdkError: initialState.sdkError,
        cameraDimension: initialState.cameraDimension,
        currentVtoSku: NO_SELECT,
        activeCategory: NO_CATEGORY,
        activeSubcategory: NO_SUBCATEGORY,
        displayLearnMoreLink: false,
        currentSkuType: NO_SELECT,
        skuApplied: {},
    })),
    on(vtoActions.launchCamera, (state) => ({
        ...state,
        modalStatus: VtoModalStatus.CAMERA_LOADING,
    })),
    on(vtoActions.onAir, (state) => ({
        ...state,
        sdkError: VtoSdkError.NO_ERROR,
        modalStatus: VtoModalStatus.CAMERA_LOADED,
    })),
    on(vtoActions.catchSdkError, (state, { sdkError }) => ({
        ...state,
        modalStatus: VtoModalStatus.ON_ERROR,
        sdkError,
    })),
    on(vtoActions.changeScreenOrientation, changeScreenOrientationReducer),
    on(
        vtoActions.fetchLocalizedVtoCategoriesSuccess,
        fetchLocalizedVtoCategoriesSuccessReducer,
    ),
    on(
        vtoActions.fetchLocalizedVtoCategories,
        fetchLocalizedVtoCategoriesReducer,
    ),
    on(
        vtoActions.updateSelectedSkuVariantOnDisplay,
        updateSelectedSkuVariantOnDisplayReducer,
    ),
    on(vtoActions.updateActiveCategory, updateActiveCategoryReducer),
    on(vtoActions.updateActiveSubcategory, updateActiveSubcategoryReducer),
    on(vtoActions.updatePageType, (state, { pageType }) => ({
        ...state,
        pageType,
    })),
    on(vtoActions.updateCurrentVtoType, (state, { currentVtoType }) => ({
        ...state,
        localizedVtoCategories: initialState.localizedVtoCategories,
        currentVtoType,
    })),
);

export const reducer = (state: VtoState | undefined, action: Action) =>
    vtoReducer(state, action);
